import { HOLD_REASON_LABEL, STATUS_LABEL, needsEstimate, type TicketStatus } from '@veta-desk/domain';
import type { AuditEntry, Ticket } from '../lib/api';
import { dateTime, relativeTime, shortDate } from '../lib/format';

// Línea fina de etapas con hitos (detalle del ticket).
//  · punto lleno = etapa cumplida (con fecha) · anillo = etapa actual · hueco = lo que falta
//  · la espera no es una etapa: se marca en naranja sobre la etapa actual
//  · un final alterno (rechazado, cancelado, convertido) sustituye al final normal y apaga el recorrido

const INC_STEPS: TicketStatus[] = ['new', 'assigned', 'in_progress', 'resolved', 'closed'];
const EST_STEPS: TicketStatus[] = ['new', 'assigned', 'estimating', 'pending_approval', 'approved', 'in_progress', 'resolved', 'closed'];
const ALT_ENDS: TicketStatus[] = ['cancelled', 'rejected', 'converted'];

interface Stamp { at: string | null; by: string | undefined }

/** Última vez que el ticket llegó a cada estado, según el historial. */
function stamps(audit: AuditEntry[]): Map<TicketStatus, Stamp> {
  const map = new Map<TicketStatus, Stamp>();
  for (const e of audit) { // el historial viene del más reciente al más antiguo
    if (e.action === 'created' && !map.has('new')) map.set('new', { at: e.at, by: e.actor?.name });
    for (const c of e.changes) {
      if (c.field === 'status' && typeof c.after === 'string' && !map.has(c.after as TicketStatus)) {
        map.set(c.after as TicketStatus, { at: e.at, by: e.actor?.name });
      }
    }
  }
  return map;
}

export type Kind = 'past' | 'now' | 'hold' | 'end' | 'future' | 'faded';
interface Node { status: TicketStatus; kind: Kind; note: string; title?: string }

function buildNodes(ticket: Ticket, audit: AuditEntry[]): Node[] {
  const base = needsEstimate(ticket.type) ? EST_STEPS : INC_STEPS;
  const seen = stamps(audit);
  const stampNote = (s: TicketStatus) => shortDate(seen.get(s)?.at);
  const stampTitle = (s: TicketStatus) => {
    const st = seen.get(s);
    return st ? `${STATUS_LABEL[s]} · ${st.by ?? ''} · ${dateTime(st.at)}` : undefined;
  };

  if (ALT_ENDS.includes(ticket.status)) {
    const reached = base.filter(s => seen.has(s));
    return [
      ...reached.map(s => ({ status: s, kind: 'faded' as Kind, note: stampNote(s), title: stampTitle(s) })),
      { status: ticket.status, kind: 'end', note: `${stampNote(ticket.status)} · fin`, title: stampTitle(ticket.status) }
    ];
  }

  const current = ticket.status === 'on_hold' ? (ticket.resumeTo ?? 'in_progress') : ticket.status;
  const idx = Math.max(0, base.indexOf(current));
  return base.map((s, i) => {
    if (i < idx) return { status: s, kind: 'past', note: stampNote(s), title: stampTitle(s) };
    if (i > idx) return { status: s, kind: 'future', note: '' };
    if (ticket.status === 'on_hold') {
      return { status: s, kind: 'hold', note: ticket.holdReason ? HOLD_REASON_LABEL[ticket.holdReason] : 'En espera',
        title: `En espera desde ${dateTime(seen.get('on_hold')?.at ?? null)}` };
    }
    const since = seen.get(s)?.at ?? ticket.updatedAt;
    return { status: s, kind: s === 'closed' ? 'end' : 'now', note: s === 'closed' ? shortDate(since) : relativeTime(since), title: stampTitle(s) };
  });
}

const DOT: Record<Kind, string> = {
  past: 'h-2.5 w-2.5 bg-accent',
  faded: 'h-2.5 w-2.5 bg-[#B9B5AE]',
  now: 'h-3 w-3 border-[3px] border-accent bg-white ring-4 ring-accent-soft',
  hold: 'h-3 w-3 border-[3px] border-[#C2410C] bg-white ring-4 ring-[#FCEBDD]',
  end: 'h-3 w-3 border-[3px] border-[#6B6760] bg-white ring-4 ring-[#EFEDE9]',
  future: 'h-3 w-3 border-2 border-[#D6D2CA] bg-white'
};
const LABEL: Record<Kind, string> = {
  past: 'text-[#3F3D39]', faded: 'text-muted', future: 'text-muted',
  now: 'font-semibold text-ink', hold: 'font-semibold text-ink', end: 'font-semibold text-ink'
};
const NOTE: Record<Kind, string> = {
  past: 'text-[#A29E96]', faded: 'text-[#A29E96]', future: '',
  now: 'text-accent', hold: 'text-[#9A3412]', end: 'text-[#6B6760]'
};

export function StageLine({ ticket, audit }: { ticket: Ticket; audit: AuditEntry[] }) {
  const nodes = buildNodes(ticket, audit).map(x => ({ key: x.status, label: STATUS_LABEL[x.status], kind: x.kind, note: x.note, title: x.title }));
  return <ProgressSteps nodes={nodes} label="Etapas del ticket" />;
}

export interface ProgressNode { key: string; label: string; kind: Kind; note: string; title?: string }

/** Línea de etapas genérica (ticket y cambio usan la misma, para que se vean igual). */
export function ProgressSteps({ nodes, label }: { nodes: ProgressNode[]; label: string }) {
  const n = nodes.length;
  const activeIdx = nodes.findIndex(x => x.kind === 'now' || x.kind === 'hold' || x.kind === 'end');
  const done = activeIdx < 0 ? 0 : activeIdx;
  const ended = nodes.some(x => x.kind === 'faded');
  const half = 100 / (2 * n);

  return (
    <nav aria-label={label}>
      {/* Horizontal (pantallas medianas y grandes) */}
      <ol className="relative hidden sm:grid" style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
        <span aria-hidden className="absolute top-[5px] h-0.5 bg-line" style={{ left: `${half}%`, right: `${half}%` }} />
        <span aria-hidden className={`absolute top-[5px] h-0.5 ${ended ? 'bg-[#B9B5AE]' : 'bg-accent'}`}
          style={{ left: `${half}%`, width: `${(100 / n) * done}%` }} />
        {nodes.map(x => (
          <li key={x.key} title={x.title} aria-current={x.kind === 'now' || x.kind === 'hold' ? 'step' : undefined}
            className="relative flex flex-col items-center px-1 text-center">
            <span className="flex h-3 items-center"><span className={`z-[1] block rounded-full ${DOT[x.kind]}`} /></span>
            <span className={`mt-2 text-xs leading-tight ${LABEL[x.kind]}`}>{x.label}</span>
            {x.note && <span className={`mt-0.5 text-[11px] leading-tight ${NOTE[x.kind]}`}>{x.note}</span>}
          </li>
        ))}
      </ol>

      {/* Vertical (celular) */}
      <ol className="flex flex-col sm:hidden">
        {nodes.map((x, i) => (
          <li key={x.key} title={x.title} className="relative flex gap-3 pb-3 last:pb-0">
            {i < n - 1 && <span aria-hidden className={`absolute left-[5px] top-3 h-full w-0.5 ${i < done ? (ended ? 'bg-[#B9B5AE]' : 'bg-accent') : 'bg-line'}`} />}
            <span className="flex h-4 w-3 items-center justify-center"><span className={`z-[1] block rounded-full ${DOT[x.kind]}`} /></span>
            <span className="text-xs leading-4">
              <span className={LABEL[x.kind]}>{x.label}</span>
              {x.note && <span className={`ml-2 text-[11px] ${NOTE[x.kind]}`}>{x.note}</span>}
            </span>
          </li>
        ))}
      </ol>
    </nav>
  );
}
